import { useMemo, useState } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEnvelopeTemplates } from '@apps/card/routes/CardCustomizer/steps/Envelope/useEnvelopeTemplates';
import { useProductsWithStatus } from '@apps/card/hooks/useProducts';
import { useCreateDraftAndRedirectToEditor } from '@apps/card/routes/PublicShop/hooks/useCreateDraftAndRedirectToEditor';
import { useCreateStationeryDraftMutation } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/ProductDetailsPage.graphql.generated';
import { useExistingDraftsWarningContext } from '@apps/card/components/ExistingDraftsWarning';
import { useRestrictedInternationalShippingWarningContext } from '@apps/card/components/RestrictedInternationalShippingWarning';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { addSuppressedError } from '@shared/utils/logger';
import type { CustomizeEnvelopeTemplate } from '@apps/card/types/stationeryTemplate.types';
import type { DraftJson } from '@apps/card/routes/CardCustomizer';

/**
 * The category the carrier template is seeded under. It is a PRODUCT category, not a design one —
 * `stationeryEnvelopeTemplates` returns exactly one row carrying it, alongside the many
 * `envelope_return` / `envelope_recipient` artwork rows the design picker reads.
 */
const CARRIER_CATEGORY = 'envelope';

/**
 * Everything needed to start a standalone-envelope order.
 *
 * Both lookups reuse queries the app already issues — no new document, and no lookup by hardcoded
 * id, which would not survive an environment rebuild. The carrier is enabled and approved —
 * `stationeryEnvelopeTemplates` filters on both, so a disabled carrier would break draft creation.
 * What keeps it out of the gallery is the backend's category exclusion, not its flags; it rides
 * in the envelope-template list the customizer's design picker already fetches.
 */
export const useEnvelopeEntry = () => {
  const { templates, loading: templatesLoading } = useEnvelopeTemplates();
  const { products, isLoading: productsLoading } = useProductsWithStatus();

  const carrier = useMemo(() => {
    const rows = templates.filter(t => t.category === CARRIER_CATEGORY);
    // Fail loudly rather than taking [0]: more than one carrier means the seed has drifted, and
    // silently picking one would attach drafts to an arbitrary template.
    return rows.length === 1 ? rows[0] : undefined;
  }, [templates]);

  const product = useMemo(() => products.find(p => p.productType === 'envelope'), [products]);

  const isLoading = templatesLoading || productsLoading;
  const isResolved = !!carrier && !!product;

  return { carrier, product, isLoading, isResolved };
};

/**
 * Starts an envelope order.
 *
 * Two creation paths, chosen by `eventHandle` rather than by the mount, because the difference is
 * about routing rather than auth. The customizer is `public: false`, so the URL it lives at differs:
 *
 * - **With a handle** (dashboard) `goToCustomizeDraft` already builds
 *   `/<handle>/edit/cards/customize/<id>`. This path also runs the restricted-shipping and
 *   existing-draft guards, exactly as `useCustomizePaperCard` does for cards.
 * - **Without one** (public shop) the shared creator prefixes `/event/edit` to a handle-less path.
 *   Prefixing a path that already contains a handle produces `/event/edit/<handle>/edit/...`, which
 *   matches no route — so the shared creator cannot serve the dashboard, and this is the split.
 *   No guards, matching how the public shop's own customize override behaves for cards.
 *
 * `onUnauthenticated` is injected rather than decided here, mirroring `ShopRouter`'s
 * `onCustomizeClickCardOverride`: signing in means opening a modal owned by `PublicShopLoginProvider`, which
 * `PublicShop` mounts and the dashboard does not. It receives a `CustomizeTemplateData`, so the
 * existing stash and `useCreateDraftAndRedirectAfterEventCreation` complete the draft after signup
 * with no envelope-specific code of their own.
 */
export const useStartEnvelopeOrder = (onUnauthenticated?: (data: CustomizeEnvelopeTemplate & { priceConfigId?: string }) => void) => {
  const { carrier, product, isLoading: lookupsLoading, isResolved } = useEnvelopeEntry();
  const { isLoggedIn, isCurrentUserSettled, hasInitializedOnce, currentUser } = useAuth();
  const { eventMemberships, loading: membershipsLoading } = useEventMemberships();
  const { eventHandle, goToCustomizeDraft } = useCardsRouterContext();
  const { eventInfo } = useEventInfo();
  const { createDraftAndRedirectToEditor } = useCreateDraftAndRedirectToEditor('envelopeEntry');
  // `SmartRoutingData` is the burger menu's copy of the drafts list; without the refetch its card
  // links keep pointing at the browse gallery until a reload. Same reasoning as `useCustomizePaperCard`.
  const [createStationeryDraft] = useCreateStationeryDraftMutation({
    refetchQueries: () => ['GetDigitalAndPaperDraftsByEventId', 'GetExistingEventStationeryDraftsWithTemplate', 'SmartRoutingData'],
  });
  const { checkForExistingDraftOrCreateNew, isLoading: existingDraftsLoading } = useExistingDraftsWarningContext();
  const { checkIfUserIsRestricted } = useRestrictedInternationalShippingWarningContext();
  const { cardDraftCreated, existingDraftsWarningDialog, restrictedInternationalShippingWarningDialog } = useCardTelemetry({});

  // The route fires on mount rather than on a click, so every input it reads has to be settled
  // first — and it fires exactly once, because `useEventCallback` keeps the identity stable. Acting
  // a tick early is not a near miss that corrects itself; it is the final answer:
  //
  // - auth and memberships: an unsettled read means `canStart` is false, so a signed-in user gets
  //   the sign-in modal or a bounce.
  // - existing drafts: an unsettled map answers "no drafts" for everything, silently creating a
  //   duplicate instead of offering the one the user already has.
  //
  // A failed `me` has to be treated as settled, or this never clears: `failure` is terminal, so
  // `isCurrentUserSettled` stays false forever — and `useEventMemberships` folds that same flag
  // into its own `loading`, so the wait below would be pinned true too. On a failed verdict
  // `canStart` is false and the visitor is offered sign-in, exactly as if they were logged out.
  const hasAuthFailed = currentUser.requestStatus === 'failure';
  const isLoading = lookupsLoading || (!hasAuthFailed && (!hasInitializedOnce || membershipsLoading)) || existingDraftsLoading;

  // An event is as load-bearing as a session: the draft is attached to one, and the Recipients step
  // reads its guest list.
  const canStart = isCurrentUserSettled && isLoggedIn && eventMemberships.length > 0;

  // Nothing on the page changes while creation is in flight, so the only sign of it is the
  // button's own spinner.
  //
  // Clearing it is the whole of what an abort does. Before the PDP this hook also raised an
  // `aborted` flag that the route redirected on, because the route was a bare loader and any stop
  // without a navigation left the shopper staring at a spinner. With a real page underneath, that
  // redirect became actively wrong: dismissing the restricted-shipping or existing-drafts dialog
  // is a considered "no, not this", and redirecting ejected the shopper off the product page
  // instead of handing back a working button. Every path now just re-enables the CTA, which is
  // what `useCustomizePaperCard` already does for a card PDP.
  const [isCreating, setIsCreating] = useState(false);
  const abort = useEventCallback(() => setIsCreating(false));

  const startEnvelopeOrder = useEventCallback((quantity?: number) => {
    // `isCreating` disables the CTA (`ButtonV2` treats `loading` as disabled), and React 17
    // flushes that synchronously at the end of the click, so a second click already lands on a
    // dead button. The guard is here for React 18: under concurrent rendering the flush is no
    // longer guaranteed before the next event, and a double-click would create two drafts. Same
    // reasoning as the copy-before-delete note in `FullPageLoaderProvider.stopTrackingKey`.
    if (!carrier || !product || isCreating) return;

    const carrierJson = carrier.themeJSON as DraftJson;

    const template = {
      id: carrier.id,
      // The carrier blob, with only the PDP's chosen quantity written over it. It deliberately
      // carries no `card.front`, which is how card_service tells an envelope draft from a card
      // one, so nothing here may add one to satisfy a type. `useValidatedPaperDraft` fills in the
      // envelope defaults on open. A missing quantity keeps the carrier's own default, which is
      // what the PDP seeded its picker from in the first place.
      themeJSON: quantity == null ? carrierJson : { ...carrierJson, customizations: { ...carrierJson.customizations, quantity } },
      category: StationeryTemplateCategoryEnum.envelope,
      themeId: carrier.themeId,
      // `StationeryEnvelopeTemplate` carries no `premium` field, and the carrier is not a design
      // anyone pays extra for.
      premium: false,
    };
    const data: CustomizeEnvelopeTemplate & { priceConfigId?: string } = { format: 'envelope', priceConfigId: product.priceConfigId, template };

    if (!canStart) {
      onUnauthenticated?.(data);
      return;
    }

    setIsCreating(true);

    if (!eventHandle) {
      createDraftAndRedirectToEditor(data, abort);
      return;
    }

    checkIfUserIsRestricted(
      StationeryDraftFormat.print,
      { themeId: template.themeId, category: template.category },
      () => restrictedInternationalShippingWarningDialog.enter(),
      () =>
        checkForExistingDraftOrCreateNew(
          template.id,
          async existingDraftsCount => {
            try {
              const response = await createStationeryDraft({
                variables: { payload: { eventId: eventInfo?.eventId, cardJSON: template.themeJSON, stationeryTemplateId: template.id, priceConfigId: product.priceConfigId } },
              });
              const draftId = response.data?.createStationeryDraft.id;
              if (!draftId) {
                abort();
                return;
              }

              cardDraftCreated({
                draftId,
                themeId: template.themeId,
                stationeryTemplateCategory: template.category,
                page: 'envelopeEntry',
                existingDraftsCount,
                isFavorite: false,
                isDigital: false,
                isPremiumDesign: false,
                format: 'envelope',
              });
              goToCustomizeDraft(draftId);
            } catch (error) {
              // No error UI on this path, but recorded rather than swallowed — same as
              // `useCustomizePaperCard`. Unlike that hook, this one must also hand the user back
              // somewhere: its page is a loader, not a modal over a page that is still there.
              addSuppressedError(error, { context: 'useStartEnvelopeOrder', themeId: template.themeId });
              abort();
            }
          },
          () => existingDraftsWarningDialog.enter(),
          false, // isDigital
          abort,
        ),
      abort,
    );
  });

  // `canOfferSignIn` lets the route tell "cannot start, but sign-in is on offer here" apart from
  // "cannot start and there is nothing to offer" — the dashboard, which an unauthenticated visitor
  // should never have reached.
  return { carrier, product, startEnvelopeOrder, isLoading, isResolved, isCreating, canStart, canOfferSignIn: !!onUnauthenticated };
};
